import { rem, useToasts } from "@certn/cascadia";
import { Container, Grid, GridItem, Text } from "@certn/cascadia-react";
import { ActionBar } from "@certn/cascadia-react/components/action-bar/ActionBar";
import { slugify, useGoogleAnalytics } from "@certn/utils";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/browser";
import { Form, Formik, FormikValues } from "formik";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { object } from "yup";

import { SectionRequirementsList } from "./SectionRequirementsList";
import { SectionProps } from "./types";
import { ApplicationContext } from "../Application.context";
import { CheckEngineAPIValidationError } from "../errors";
import { getField } from "../fields";
import { getRule } from "../validationRules";

export function BaseSection({ section, onNext, onBack }: SectionProps) {
  const { t } = useTranslation();
  const sectionFields = useMemo(
    () => section.fields.map(getField).filter(Boolean),
    [section, getField]
  );
  const { formikRef, saveData } = useContext(ApplicationContext);
  const { show } = useToasts();
  const { gaTrackEvent } = useGoogleAnalytics();

  // combine schemas from all fields for data validation
  const validationSchema = sectionFields.reduce((prev, { field, schema }) => {
    if (!schema) {
      return prev;
    }
    return prev.concat(
      object().shape({
        [field.name]: schema,
      })
    );
  }, object());

  const unsavedValues = section.unsavedValues;

  const currentValues = sectionFields.reduce(
    (prev, { field, initialValue }) => ({
      ...prev,
      [field.name]: initialValue,
    }),
    {}
  );

  const initialValues: object = unsavedValues ? unsavedValues : currentValues;

  const handleSubmit = async () => {
    try {
      await saveData({ sectionKey: section.key, skipValidation: true });
      gaTrackEvent("application_next", { section: section.key });
      onNext();
    } catch (e) {
      show({ title: t("Something went wrong"), type: "error" });

      if (e instanceof CheckEngineAPIValidationError) {
        Sentry.captureException(e);
      }
    }
  };

  const handleBack = async () => {
    gaTrackEvent("application_back", { section: section.key });

    onBack && onBack();
  };

  const handleValidate = (values: FormikValues) => {
    const errors: Record<string, unknown> = {};

    sectionFields.map(({ field }) => {
      // Validate using "validation_rules"
      field.validation_rules.forEach((validationRule) => {
        const rule = getRule(validationRule);

        if (!rule) return;

        if (!rule.isValid(values[field.name]) && validationRule.required) {
          errors[validationRule.type] = "Invalid";
        }
      });
    });

    return errors;
  };

  return (
    <Container>
      <Formik
        innerRef={formikRef}
        key={section.key}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={handleValidate}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, errors, values, submitForm, isValid, touched }) => {
          const preSaveField = sectionFields?.find(
            ({ saveButtonOverride }) => saveButtonOverride
          );

          // pre save actions live in the saveButtonOverride fn inside index.tsx
          // if a modal is returned this will override the Next Step button in ActionBar
          const saveButtonOverride = preSaveField?.saveButtonOverride?.({
            values,
            submitForm,
            submitFormWithoutValidation: handleSubmit,
          });

          return (
            <Form>
              <Grid>
                <GridItem alignItems="left">
                  <Text
                    as="h1"
                    style="form/page-title"
                    className={css({
                      marginTop: rem("space-6"),
                    })}
                    testID={`section-title-${slugify(section.title)}`}
                  >
                    {section.title}
                  </Text>
                  <Text
                    style="form/body-primary"
                    color="text-weak"
                    className={css({ marginTop: rem("space-2") })}
                    testID={`section-description-${slugify(section.title)}`}
                  >
                    {section.subtitle}
                  </Text>
                </GridItem>
              </Grid>

              {sectionFields.map(({ inputElement, field }) => (
                <Grid
                  key={field.name}
                  aria-label={field.label}
                  className={css({
                    marginTop: rem("space-6"),
                    marginBottom: rem("space-6"),
                  })}
                >
                  {inputElement}
                </Grid>
              ))}

              <Grid>
                <GridItem aria-label={t("Section Requirements")}>
                  <SectionRequirementsList
                    fields={sectionFields}
                    values={values}
                    errors={errors}
                  />
                </GridItem>

                <GridItem>
                  <ActionBar
                    primaryText={t("Next step")}
                    onPrimaryClick={submitForm}
                    saveButtonOverride={saveButtonOverride}
                    backText={t("Back")}
                    onBackClick={onBack && handleBack}
                    isLoading={isSubmitting}
                  />
                </GridItem>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
}
